// Enums para Plano de Cuidado
export enum CarePlanStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  ON_HOLD = 'ON_HOLD',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export enum GoalStatus {
  PROPOSED = 'PROPOSED',
  IN_PROGRESS = 'IN_PROGRESS',
  ACHIEVED = 'ACHIEVED',
  NOT_ACHIEVED = 'NOT_ACHIEVED',
  CANCELLED = 'CANCELLED',
}

export enum ActivityStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  ON_HOLD = 'ON_HOLD',
}

export enum ActivityCategory {
  MEDICATION = 'MEDICATION',
  PROCEDURE = 'PROCEDURE',
  OBSERVATION = 'OBSERVATION',
  EDUCATION = 'EDUCATION',
  OTHER = 'OTHER',
}

export enum ActivityFrequency {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  AS_NEEDED = 'AS_NEEDED',
}

export enum ResponsibleRole {
  DOCTOR = 'DOCTOR',
  NURSE = 'NURSE',
  PATIENT = 'PATIENT',
  CAREGIVER = 'CAREGIVER',
}

// Configuração de labels e cores para status do plano
export const CarePlanStatusConfig: Record<CarePlanStatus, { label: string; color: string; bgColor: string }> = {
  [CarePlanStatus.DRAFT]: { label: 'Rascunho', color: '#6b7280', bgColor: '#f3f4f6' },
  [CarePlanStatus.ACTIVE]: { label: 'Ativo', color: '#22c55e', bgColor: '#f0fdf4' },
  [CarePlanStatus.ON_HOLD]: { label: 'Em Espera', color: '#eab308', bgColor: '#fefce8' },
  [CarePlanStatus.COMPLETED]: { label: 'Concluído', color: '#3b82f6', bgColor: '#eff6ff' },
  [CarePlanStatus.CANCELLED]: { label: 'Cancelado', color: '#ef4444', bgColor: '#fef2f2' },
};

// Configuração de labels para status de meta
export const GoalStatusConfig: Record<GoalStatus, { label: string; color: string }> = {
  [GoalStatus.PROPOSED]: { label: 'Proposta', color: '#6b7280' },
  [GoalStatus.IN_PROGRESS]: { label: 'Em Andamento', color: '#3b82f6' },
  [GoalStatus.ACHIEVED]: { label: 'Alcançada', color: '#22c55e' },
  [GoalStatus.NOT_ACHIEVED]: { label: 'Não Alcançada', color: '#ef4444' },
  [GoalStatus.CANCELLED]: { label: 'Cancelada', color: '#6b7280' },
};

// Configuração de labels para status de atividade
export const ActivityStatusConfig: Record<ActivityStatus, { label: string; color: string }> = {
  [ActivityStatus.NOT_STARTED]: { label: 'Não Iniciada', color: '#6b7280' },
  [ActivityStatus.IN_PROGRESS]: { label: 'Em Andamento', color: '#3b82f6' },
  [ActivityStatus.COMPLETED]: { label: 'Concluída', color: '#22c55e' },
  [ActivityStatus.CANCELLED]: { label: 'Cancelada', color: '#ef4444' },
  [ActivityStatus.ON_HOLD]: { label: 'Em Espera', color: '#eab308' },
};

// Configuração de labels para categoria de atividade
export const ActivityCategoryConfig: Record<ActivityCategory, { label: string }> = {
  [ActivityCategory.MEDICATION]: { label: 'Medicação' },
  [ActivityCategory.PROCEDURE]: { label: 'Procedimento' },
  [ActivityCategory.OBSERVATION]: { label: 'Observação' },
  [ActivityCategory.EDUCATION]: { label: 'Educação' },
  [ActivityCategory.OTHER]: { label: 'Outro' },
};

// Configuração de labels para frequência
export const ActivityFrequencyConfig: Record<ActivityFrequency, { label: string }> = {
  [ActivityFrequency.DAILY]: { label: 'Diário' },
  [ActivityFrequency.WEEKLY]: { label: 'Semanal' },
  [ActivityFrequency.MONTHLY]: { label: 'Mensal' },
  [ActivityFrequency.AS_NEEDED]: { label: 'Quando Necessário' },
};

// Configuração de labels para responsável
export const ResponsibleRoleConfig: Record<ResponsibleRole, { label: string }> = {
  [ResponsibleRole.DOCTOR]: { label: 'Médico' },
  [ResponsibleRole.NURSE]: { label: 'Enfermeiro' },
  [ResponsibleRole.PATIENT]: { label: 'Paciente' },
  [ResponsibleRole.CAREGIVER]: { label: 'Cuidador' },
};

// Interfaces
export interface CarePlanGoal {
  code: string;
  description: string;
  targetValue?: string;
  currentValue?: string;
  targetDate?: string;
  status: GoalStatus;
  notes?: string;
}

export interface CarePlanActivity {
  code: string;
  description: string;
  category: ActivityCategory;
  frequency: ActivityFrequency;
  startDate?: string;
  endDate?: string;
  status: ActivityStatus;
  responsibleRole: ResponsibleRole;
  notes?: string;
}

export interface CarePlanResponse {
  id: string;
  patientId: string;
  patientName: string;
  title: string;
  description?: string;
  status: CarePlanStatus;
  statusLabel: string;
  startDate?: string;
  endDate?: string;
  goals: CarePlanGoal[];
  activities: CarePlanActivity[];
  addressedProblems?: string[];
  notes?: string;
  createdByName: string;
  reviewedByName?: string;
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
  overdue: boolean;
  goalsCount: number;
  goalsAchievedCount: number;
  activitiesCount: number;
  activitiesCompletedCount: number;
}

export interface CreateCarePlanRequest {
  patientId: string;
  title: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  goals?: CarePlanGoal[];
  activities?: CarePlanActivity[];
  addressedProblems?: string[];
  notes?: string;
}

export interface UpdateCarePlanRequest {
  title?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  goals?: CarePlanGoal[];
  activities?: CarePlanActivity[];
  addressedProblems?: string[];
  notes?: string;
}

export interface UpdateGoalRequest {
  code: string;
  status?: GoalStatus;
  currentValue?: string;
  notes?: string;
}

export interface UpdateActivityRequest {
  code: string;
  status?: ActivityStatus;
  notes?: string;
}
